// src/features/faq/components/RewardsManagementFAQs.tsx

import React from 'react';
import { Gift, Plus, Edit2, Trash2, Image, CheckCircle, XCircle } from 'lucide-react'; // Sliders no se usa aquí, se puede quitar

export interface RewardsFAQItem {
  id: string;
  question: string;
  answer: React.ReactNode;
}

interface RewardsFAQData {
  id: string;
  title: string;
  icon: React.ReactNode;
  description: string;
  faqs: RewardsFAQItem[];
}

export const RewardsManagementFAQs = (navigateCallback: (path: string) => void): RewardsFAQData => {
  return {
    id: 'rewards', // ID de la categoría cambiado
    title: 'Recompensas', // Título de la categoría cambiado
    icon: <Gift className="h-5 w-5 text-accent" />, // Icono Gift, color azul como en tu captura
    description: 'Aprende a crear, editar y administrar los premios que ofreces a tus clientes.', // Descripción actualizada
    faqs: [
      {
        id: 'rewards-purpose',
        // Pregunta actualizada para ser más general
        question: '¿Para qué sirve la sección "Recompensas" y qué puedo gestionar allí?',
        answer: (
          <div>
            {/* Respuesta actualizada para reflejar el nombre de la sección y la página */}
            <p>La sección "Recompensas" (accesible desde el menú lateral) te lleva a la página de "Gestión de Recompensas". Esta página es donde configuras los premios que tus clientes pueden canjear utilizando los puntos que han acumulado. Desde aquí puedes:</p>
            <ul className="list-disc ml-5 mt-2 space-y-1">
              <li>Crear nuevas recompensas atractivas para tus clientes.</li>
              <li>Editar los detalles de recompensas existentes (nombre, descripción, puntos, imagen, estado).</li>
              <li>Activar o desactivar recompensas según tu estrategia o disponibilidad.</li>
              <li>Eliminar recompensas que ya no ofreces (se realiza un borrado suave).</li>
              <li>Visualizar y organizar tu catálogo de recompensas, filtrando por activas o inactivas.</li>
            </ul>
            <p className="mt-2 text-content-2">Un catálogo de recompensas bien gestionado es esencial para mantener el interés y la participación de tus clientes en el programa de lealtad.</p>
            <div className="mt-2">
              {/* El botón puede seguir diciendo "Gestión de Recompensas" ya que ese es el título de la página destino */}
              <button onClick={() => navigateCallback('/dashboard/rewards')} className="inline-flex items-center text-sm text-accent hover:text-blue-800 dark:text-blue-300">
                <Gift className="h-4 w-4 mr-1.5" />
                Ir a Gestión de Recompensas
              </button>
            </div>
          </div>
        )
      },
      {
        id: 'rewards-create',
        question: '¿Cómo creo una nueva recompensa?',
        answer: (
          <div>
            <p>Para añadir una nueva recompensa a tu programa:</p>
            <ol className="list-decimal ml-5 mt-2 space-y-1">
              <li>Ve a la página de <button onClick={() => navigateCallback('/dashboard/rewards')} className="text-accent hover:text-blue-800 dark:text-blue-300 font-medium">Gestión de Recompensas</button>.</li>
              <li>Haz clic en el botón "<Plus className="inline h-4 w-4 mr-1"/>Nueva Recompensa".</li>
              <li>Se abrirá un formulario donde deberás completar los siguientes campos:
                <ul className="list-disc ml-6 mt-1">
                  <li><strong>Título:</strong> Un nombre corto y atractivo para la recompensa (ej. "Café Gratis", "Descuento del 10%").</li>
                  <li><strong>Descripción (Opcional):</strong> Detalles adicionales sobre la recompensa, condiciones, etc.</li>
                  <li><strong>Costo de la recompensa:</strong> En el modelo de <strong>Puntos</strong>, los puntos que el cliente necesita para canjearla; en el modelo <strong>Cashback</strong>, el costo en dinero ($). El formulario ajusta la etiqueta según tu configuración.</li>
                  <li><strong>Imagen (obligatoria):</strong> Debes subir una imagen desde tu dispositivo o ingresar una URL; no podrás guardar la recompensa sin imagen. ¡Una buena imagen la hace más atractiva!</li>
                  <li><strong>Recompensa activa:</strong> Marca esta casilla si quieres que la recompensa esté visible y disponible para canjear por los clientes.</li>
                </ul>
              </li>
              <li>Una vez completados los datos, haz clic en "Guardar".</li>
            </ol>
            <p className="mt-2 text-content-2">Tu nueva recompensa aparecerá en la lista y, si está activa, tus clientes podrán verla (dependiendo de cómo se muestren las recompensas en la interfaz del cliente).</p>
          </div>
        )
      },
      {
        id: 'rewards-cost-model',
        question: '¿El costo de la recompensa se mide en puntos o en dinero?',
        answer: (
          <div>
            <p>Depende del <strong>modelo de lealtad</strong> que tengas configurado en <button onClick={() => navigateCallback('/dashboard/business?tab=rewards')} className="text-accent hover:text-blue-800 dark:text-blue-300 font-medium">Mi Negocio</button>:</p>
            <ul className="list-disc ml-5 mt-2 space-y-1">
              <li><strong>Modelo Puntos:</strong> el costo se expresa en <strong>puntos</strong> y la columna de la lista se titula "Puntos".</li>
              <li><strong>Modelo Cashback:</strong> el costo se expresa en <strong>dinero ($)</strong> (admite decimales) y la columna se titula "Costo ($)".</li>
            </ul>
            <p className="mt-2 text-content-2">El formulario y la lista cambian automáticamente las etiquetas según el modelo activo, así que siempre verás la unidad correcta.</p>
          </div>
        )
      },
      {
        id: 'rewards-edit',
        question: '¿Cómo puedo editar una recompensa existente?',
        answer: (
          <div>
            <p>Si necesitas modificar los detalles de una recompensa ya creada:</p>
            <ol className="list-decimal ml-5 mt-2 space-y-1">
              <li>Dirígete a la <button onClick={() => navigateCallback('/dashboard/rewards')} className="text-accent hover:text-blue-800 dark:text-blue-300 font-medium">Gestión de Recompensas</button>.</li>
              <li>En la lista de recompensas, localiza la que deseas modificar.</li>
              <li>Haz clic en el icono de editar (<Edit2 className="inline h-4 w-4 text-content-2"/>) asociado a esa recompensa.</li>
              <li>Se abrirá el mismo formulario que usaste para crearla, pero con los datos actuales cargados. Modifica los campos que necesites (título, puntos, imagen, estado, etc.).</li>
              <li>Haz clic en "Guardar" para aplicar los cambios.</li>
            </ol>
          </div>
        )
      },
      {
        id: 'rewards-delete',
        question: '¿Cómo elimino una recompensa? ¿Se borra para siempre?',
        answer: (
          <div>
            <p>Para quitar una recompensa que ya no deseas ofrecer:</p>
            <ol className="list-decimal ml-5 mt-2 space-y-1">
              <li>Ve a la <button onClick={() => navigateCallback('/dashboard/rewards')} className="text-accent hover:text-blue-800 dark:text-blue-300 font-medium">Gestión de Recompensas</button>.</li>
              <li>Encuentra la recompensa en la lista.</li>
              <li>Haz clic en el icono de eliminar (<Trash2 className="inline h-4 w-4 text-content-2"/>). Es posible que se te pida confirmar la acción.</li>
            </ol>
            <p className="mt-2 text-content-2"><strong>Importante sobre la eliminación:</strong> Las recompensas se "eliminan suavemente" (soft delete). Esto significa que no se borran permanentemente de la base de datos de inmediato, sino que se marcan como 'deleted' y se ocultan de la vista. Esto ayuda a mantener la integridad de los datos históricos si algunos clientes ya habían canjeado esa recompensa.</p>
            <p className="mt-1 text-content-2">Si deseas que una recompensa no esté disponible temporalmente, es mejor editarla y desmarcar la casilla "Recompensa activa".</p>
          </div>
        )
      },
      {
        id: 'rewards-image',
        question: '¿Qué consideraciones debo tener para la imagen de la recompensa?',
        answer: (
          <div>
            <p>Una buena imagen puede hacer tu recompensa mucho más atractiva. Ten en cuenta:</p>
            <ul className="list-disc ml-5 mt-2 space-y-1">
              <li><strong>Es obligatoria:</strong> no podrás guardar una recompensa sin imagen.</li>
              <li><strong>Subir archivo o URL:</strong> El formulario te permite subir un archivo de imagen (JPG, PNG, WEBP) desde tu computadora o pegar una URL de una imagen ya existente en internet.</li>
              <li><strong>Calidad y Dimensiones:</strong> Usa imágenes claras y de buena calidad. Un formato cuadrado o ligeramente rectangular suele funcionar bien. Evita imágenes muy pesadas para no afectar los tiempos de carga.</li>
              <li><strong>Relevancia:</strong> La imagen debe representar claramente la recompensa.</li>
              <li><strong>Actualización:</strong> Puedes cambiar la imagen de una recompensa en cualquier momento editándola.</li>
            </ul>
            <p className="mt-2 text-content-2">El sistema de carga de imagen (<Image className="inline h-4 w-4 text-content-2"/>) te guiará en el proceso de subida o ingreso de URL.</p>
          </div>
        )
      },
      {
        id: 'rewards-status',
        question: '¿Qué diferencia hay entre una recompensa "activa" e "inactiva"?',
        answer: (
          <div>
            <p>El estado de una recompensa determina su visibilidad y disponibilidad para los clientes:</p>
            <ul className="list-disc ml-5 mt-2 space-y-1">
              <li><strong className="flex items-center"><CheckCircle className="h-4 w-4 mr-1 text-green-500"/>Recompensa Activa:</strong> Está visible para tus clientes y pueden canjearla si tienen los puntos necesarios.</li>
              <li><strong className="flex items-center"><XCircle className="h-4 w-4 mr-1 text-red-500"/>Recompensa Inactiva:</strong> No está visible para los clientes y no puede ser canjeada. Es útil si quieres preparar una recompensa con antelación o pausarla temporalmente sin eliminarla.</li>
            </ul>
            <p className="mt-2 text-content-2">Puedes cambiar el estado de una recompensa en cualquier momento editándola y marcando/desmarcando la casilla "Recompensa activa".</p>
          </div>
        )
      },
      {
        id: 'rewards-sorting-filtering',
        question: '¿Puedo organizar o filtrar mi lista de recompensas en el panel de gestión?',
        answer: (
          <div>
            <p>Sí, la lista de recompensas en la página de "Gestión de Recompensas" generalmente ofrece opciones para facilitar su manejo:</p>
            <ul className="list-disc ml-5 mt-2 space-y-1">
              <li><strong>Filtrado por Estado:</strong> Sueles tener botones o un selector para ver "Todas", solo las "Activas" o solo las "Inactivas".</li>
              <li><strong>Ordenamiento:</strong> Haciendo clic en las cabeceras de algunas columnas (como "Puntos" o "Última actualización") puedes ordenar la lista de forma ascendente o descendente por ese criterio.</li>
            </ul>
            <p className="mt-2 text-content-2">Estas herramientas te ayudan a encontrar rápidamente las recompensas que buscas y a tener una mejor visión general de tu catálogo.</p>
          </div>
        )
      },
      // Aquí podrías añadir una pregunta sobre si la creación de recompensas es PRO, 
      // pero basado en RewardsManagementPage.tsx, no parece serlo directamente.
      // Ejemplo:
      // {
      //   id: 'rewards-pro-feature',
      //   question: '¿Necesito un plan PRO para crear recompensas?',
      //   answer: (
      //     <div>
      //       <p>La creación y gestión básica de recompensas está disponible en todos los planes. Sin embargo, funcionalidades avanzadas como [mencionar alguna si aplica, ej: "recompensas por niveles de cliente" o "estadísticas detalladas de canje"] podrían ser exclusivas del plan PRO.</p>
      //       <div className="mt-2">
      //         <button onClick={() => navigateCallback('/dashboard/subscription')} className="inline-flex items-center px-3 py-1.5 text-xs font-medium rounded-md text-white bg-gradient-to-r from-purple-500 to-indigo-500 hover:from-purple-600 hover:to-indigo-700">
      //           Ver Planes y Precios <ArrowRight className="ml-1.5 h-3.5 w-3.5" />
      //         </button>
      //       </div>
      //     </div>
      //   )
      // },
    ]
  };
};